import { useState } from "react";
import { motion } from "framer-motion";
import { Repeat, CheckCircle2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
    return (
        <label className="block bg-surface2 border border-line rounded-xl px-5 py-4 hover:border-gold/40 transition-all">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                {label} {required && "*"}
            </span>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full mt-1 bg-transparent text-sm focus:outline-none placeholder:text-ink-subtle"
                data-testid={testid}
            />
        </label>
    );
}

export function FinanceSection() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        car_label: "",
        down_payment: "",
        months: "",
        monthly_income: "",
        notes: "",
    });
    const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: false, error: "" });
        try {
            const payload = {
                ...form,
                down_payment: form.down_payment ? parseFloat(form.down_payment) : null,
                months: form.months ? parseInt(form.months) : null,
                monthly_income: form.monthly_income ? parseFloat(form.monthly_income) : null,
            };
            await api.post("/finance-requests", payload);
            setStatus({ loading: false, ok: true, error: "" });
            setForm({
                name: "", email: "", phone: "", car_label: "",
                down_payment: "", months: "", monthly_income: "", notes: "",
            });
        } catch (err) {
            setStatus({
                loading: false,
                ok: false,
                error: formatApiError(err?.response?.data?.detail) || err.message,
            });
        }
    };

    return (
        <section id="finance" className="relative py-28 lg:py-36" data-testid="finance-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="max-w-3xl">
                    <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-5">
                        Финансиране
                    </span>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tightest mb-8 leading-[1.05]">
                        Гъвкави условия за вашия автомобил
                    </h2>
                    <p className="text-ink-muted leading-relaxed mb-8 text-lg">
                        Партньорите ни — водещи банкови и лизингови институции — предлагат
                        бързо одобрение, конкурентни лихви и индивидуални схеми за всеки
                        клиент.
                    </p>
                    <ul className="space-y-3 text-ink-muted">
                        {[
                            "Срок до 84 месеца",
                            "Одобрение до 24 часа",
                            "Без скрити такси",
                        ].map((x) => (
                            <li key={x} className="flex items-center gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {x}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

export function TradeInSection() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        current_brand: "",
        current_model: "",
        current_year: "",
        current_mileage: "",
        current_condition: "Отлично",
        desired_car_label: "",
        notes: "",
    });
    const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: false, error: "" });
        try {
            const payload = {
                ...form,
                current_year: parseInt(form.current_year),
                current_mileage: parseInt(form.current_mileage),
            };
            await api.post("/tradein-requests", payload);
            setStatus({ loading: false, ok: true, error: "" });
            setForm({
                name: "", email: "", phone: "", current_brand: "", current_model: "",
                current_year: "", current_mileage: "", current_condition: "Отлично",
                desired_car_label: "", notes: "",
            });
        } catch (err) {
            setStatus({
                loading: false,
                ok: false,
                error: formatApiError(err?.response?.data?.detail) || err.message,
            });
        }
    };

    return (
        <section id="tradein" className="relative py-28 lg:py-36" data-testid="tradein-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-7 glass rounded-3xl p-8 lg:p-10 order-2 lg:order-1"
                        data-testid="tradein-form"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Име" value={form.name} onChange={(v) => set("name", v)} required testid="ti-name" />
                            <Field label="Имейл" type="email" value={form.email} onChange={(v) => set("email", v)} required testid="ti-email" />
                            <Field label="Телефон" value={form.phone} onChange={(v) => set("phone", v)} required testid="ti-phone" />
                            <Field label="Желан автомобил" value={form.desired_car_label} onChange={(v) => set("desired_car_label", v)} testid="ti-desired" />
                            <Field label="Текуща марка" value={form.current_brand} onChange={(v) => set("current_brand", v)} required testid="ti-brand" />
                            <Field label="Текущ модел" value={form.current_model} onChange={(v) => set("current_model", v)} required testid="ti-model" />
                            <Field label="Година" type="number" value={form.current_year} onChange={(v) => set("current_year", v)} required testid="ti-year" />
                            <Field label="Пробег (км)" type="number" value={form.current_mileage} onChange={(v) => set("current_mileage", v)} required testid="ti-mileage" />
                        </div>
                        <label className="block mt-5 bg-surface2 border border-line rounded-xl px-5 py-4">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                                Състояние
                            </span>
                            <select
                                value={form.current_condition}
                                onChange={(e) => set("current_condition", e.target.value)}
                                className="w-full mt-1 bg-surface2 text-sm focus:outline-none"
                                data-testid="ti-condition"
                            >
                                <option>Отлично</option>
                                <option>Много добро</option>
                                <option>Добро</option>
                                <option>Задоволително</option>
                            </select>
                        </label>
                        <label className="block mt-5 bg-surface2 border border-line rounded-xl px-5 py-4">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                                Бележки
                            </span>
                            <textarea
                                rows={3}
                                value={form.notes}
                                onChange={(e) => set("notes", e.target.value)}
                                className="w-full mt-2 bg-transparent text-sm focus:outline-none resize-none"
                                data-testid="ti-notes"
                            />
                        </label>

                        {status.error && <div className="mt-5 text-sm text-red-400" data-testid="ti-error">{status.error}</div>}
                        {status.ok && (
                            <div className="mt-5 flex items-center gap-2 text-sm text-gold" data-testid="ti-success">
                                <CheckCircle2 size={16} /> Получихме заявката ви за бартер. Очаквайте оценка скоро.
                            </div>
                        )}

                        <div className="mt-7 flex justify-end">
                            <button
                                type="submit"
                                disabled={status.loading}
                                className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-bg font-medium px-8 py-4 rounded-full transition-all disabled:opacity-60"
                                data-testid="ti-submit"
                            >
                                {status.loading ? "Изпращане…" : (<>Поискай оценка <Send size={16} /></>)}
                            </button>
                        </div>
                    </motion.form>

                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-5">
                            Бартер
                        </span>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tightest mb-8 leading-[1.05]">
                            Заменете своя автомобил с по-нов и по-скъп.
                        </h2>
                        <p className="text-ink-muted leading-relaxed mb-8">
                            Получете честна и прозрачна оценка на настоящия си автомобил като част
                            от стойността при покупка на новия. Безплатна и без ангажимент.
                        </p>
                        <div className="w-16 h-16 rounded-2xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                            <Repeat size={28} className="text-gold" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
